import React, { useEffect, useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Building2, GraduationCap, Search, ShieldCheck, Sparkles } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const CONFIG = {
  department: {
    plural: 'departments',
    title: 'Departments',
    eyebrow: 'Academic Structure',
    subtitle: 'Explore academic departments, programme areas, HOD profiles, resources and contact information.',
    icon: GraduationCap,
    accent: 'from-blue-700 to-cyan-500',
  },
  center: {
    plural: 'centers',
    title: 'Centers',
    eyebrow: 'Institutional Centers',
    subtitle: 'Discover specialist centers supporting learning, innovation, enterprise, health and institutional development.',
    icon: Sparkles,
    accent: 'from-teal-700 to-cyan-500',
  },
  directorate: {
    plural: 'directorates',
    title: 'Directorates',
    eyebrow: 'Strategic Directorates',
    subtitle: 'Explore directorates coordinating specialist services, planning, technology, development and institutional support.',
    icon: Building2,
    accent: 'from-indigo-700 to-violet-500',
  },
  unit: {
    plural: 'units',
    title: 'Units',
    eyebrow: 'Administrative Units',
    subtitle: 'Find institutional units, their functions, leadership, services, resources and contact channels.',
    icon: ShieldCheck,
    accent: 'from-emerald-700 to-green-500',
  },
};


const directoryFade = {
  hidden: { opacity: 0, y: 24, filter: 'blur(8px)' },
  visible: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};

const directoryStagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07, delayChildren: 0.06 } },
};

function publicImage(src) {
  if (!src) return '/fpb.png';
  return src.startsWith('http') ? src : src;
}

const STATIC_DEPARTMENTS = [
  ['School of Engineering', 'Agricultural and Bio-Environmental Engineering', '/agricultural-and-bio-environmental-engineering'],
  ['School of Engineering', 'Chemical Engineering', '/chemical-engineering'],
  ['School of Engineering', 'Civil Engineering', '/civil-engineering'],
  ['School of Engineering', 'Computer Engineering', '/computer-engineering'],
  ['School of Engineering', 'Electrical Engineering', '/electrical-engineering'],
  ['School of Engineering', 'Mechanical Engineering', '/mechanical-engineering'],
  ['School of Engineering', 'Renewable Energy Engineering', '/renewable-energy-engineering'],
  ['School of Agricultural Technology', 'Agricultural Technology', '/agricultural-technology'],
  ['School of Agricultural Technology', 'Crop Production Technology', '/crop-production-technology'],
  ['School of Agricultural Technology', 'Horticulture and Landscape Technology', '/horticulture-and-landscape-technology'],
  ['School of Agricultural Technology', 'Livestock and Animal Production Technology', '/livestock-and-animal-production-technology'],
  ['School of Basic and General Studies', 'Languages', '/languages'],
  ['School of Basic and General Studies', 'Legal Studies', '/legal-studies'],
  ['School of Basic and General Studies', 'Social Sciences', '/social-sciences'],
  ['School of Basic and General Studies', 'Mathematics', '/mathematics'],
  ['School of Financial Studies', 'Accountancy', '/accountancy'],
  ['School of Financial Studies', 'Banking and Finance', '/banking-and-finance'],
  ['School of Financial Studies', 'Taxation', '/taxation'],
  ['School of Business Administration and Management', 'Business Administration and Management', '/business-administration-and-management'],
  ['School of Business Administration and Management', 'Marketing', '/marketing'],
  ['School of Business Administration and Management', 'Public Administration', '/public-administration'],
  ['School of Business Administration and Management', 'Procurement and Supply Chain Management', '/procurement-and-supply-chain-management'],
  ['School of Information Management Technology', 'Office Technology Management', '/office-of-technology-management'],
  ['School of Information Management Technology', 'Library and Information Science', '/library-and-information-science'],
  ['School of Information Management Technology', 'Health Information Management Technology', '/health-information-management-technology'],
  ['School of Information Management Technology', 'Mass Communication', '/mass-communication'],
  ['School of Computing Technology', 'Artificial Intelligence Technology', '/artificial-intelligence-technology'],
  ['School of Computing Technology', 'Computer Science', '/computer-science'],
  ['School of Computing Technology', 'Cyber Security and Data Protection', '/cyber-security-and-data-protection'],
  ['School of Computing Technology', 'Networking and Cloud Computing', '/networking-and-cloud-computing'],
  ['School of Computing Technology', 'Software and Web Development', '/software-and-web-development'],
  ['School of Applied & Natural Science', 'Statistics', '/statistics'],
  ['School of Applied & Natural Science', 'Hospitality Management', '/hospitality-management'],
  ['School of Applied & Natural Science', 'Nutrition and Dietetics', '/nutrition-and-dietetics'],
  ['School of Applied & Natural Science', 'Food Science and Technology', '/food-science-and-technology'],
  ['School of Applied & Natural Science', 'Tourism Management Technology', '/tourism-management-technology'],
  ['School of Applied & Natural Science', 'Science Laboratory Technology', '/science-laboratory-technology'],
  ['School of Applied & Natural Science', 'Science Laboratory Technology (Biological Sciences)', '/science-laboratory-technology-biological-sciences-'],
  ['School of Applied & Natural Science', 'Science Laboratory Technology (Chemical Sciences)', '/science-laboratory-technology-chemical-sciences-'],
  ['School of Applied & Natural Science', 'Science Laboratory Technology (Physics)', '/science-laboratory-technology-physics-'],
  ['School of Environmental Studies', 'Environmental Science and Management', '/environmental-science-and-management'],
  ['School of Environmental Studies', 'Estate Management and Valuation', '/estate-management-and-valuation'],
  ['School of Environmental Studies', 'Urban and Regional Planning', '/urban-and-regional-planning'],
  ['School of Environmental Studies', 'Architectural Technology', '/architectural-technology'],
  ['School of Environmental Studies', 'Quantity Surveying', '/quantity-surveying'],
  ['School of Environmental Studies', 'Building Technology', '/building-technology'],
  ['School of Environmental Studies', 'Surveying and Geoinformatics', '/surveying-and-geoinformatics'],
  ['School of Communication and Media Studies', 'Film and Media Studies', '/film-and-media-studies'],
  ['School of Communication and Media Studies', 'Journalism and Media Studies', '/journalism-and-media-studies'],
  ['School of Communication and Media Studies', 'Strategic Communication and Media Studies', '/strategic-communication-and-media-studies'],
].map(([school, name, public_url], index) => ({
  id: `static-${index}`,
  page_type: 'department',
  name,
  slug: public_url.replace(/^\//, ''),
  parent_group: school,
  short_description: 'View the existing department page with history, leadership, programmes, admission details and contact information.',
  public_url,
  legacy: true,
}));


const STATIC_CENTERS = [
  ['Career Development Center', '/career-development-center', 'Student career guidance, employment readiness, employability support and institutional career development services.'],
  ['Entrepreneurship Center', '/enterpreneurship-center', 'Enterprise development, entrepreneurship training, innovation support and business incubation services for students and staff.'],
  ['Center for Open Distance & e-Learning (CODeL)', '/center-for-open-distance-e-learning-codel', 'Flexible learning, e-learning support, open distance education coordination and digital course delivery services.'],
  ['Medical Center', '/medical-center', 'Campus health services, student medical support, wellness guidance and emergency health response coordination.'],
  ['Climate Change & Environmental Sustainability Center', '/climate-change-environmental-sustainability-center', 'Environmental sustainability, climate awareness, green campus initiatives and research support.'],
  ['University Affiliation Center', '/university-affiliation-center', 'Affiliation coordination, partner institution support, academic linkage and external programme liaison.'],
].map(([name, public_url, short_description], index) => ({
  id: `static-center-${index}`,
  page_type: 'center',
  name,
  slug: public_url.replace(/^\//, ''),
  parent_group: 'Institutional Centers',
  short_description,
  public_url,
  hero_image: '/fpb.png',
  legacy: true,
}));

const STATIC_UNITS = [
  ['Anti-Corruption and Transparency Monitoring Unit (ACTU)', '/anti-corruption-and-transparency-monitoring-unit-actu-', 'Integrity monitoring, transparency advocacy, compliance awareness and anti-corruption support services.'],
  ['Servicom Unit', '/servicom-unit', 'Service delivery monitoring, citizen-focused support, feedback coordination and service improvement.'],
  ['Registry Unit', '/registry-unit', 'Academic and administrative records, correspondence, policy documentation and institutional registry services.'],
  ['Bursary Unit', '/bursary-unit', 'Financial administration, revenue support, payments, accounts and fiscal reporting services.'],
  ['Rectory Unit', '/rectory-unit', 'Support services for the Rector’s office, executive coordination and institutional leadership administration.'],
  ['Polytechnic Legal Unit', '/polytechnic-legal-unit', 'Legal advisory services, institutional compliance, documentation support and legal coordination.'],
  ['Security and Related Matters Unit', '/security-unit', 'Campus security coordination, safety support, incident response and related protection services.'],
  ['Planning Unit', '/planning-unit', 'Institutional planning, development coordination, reporting and administrative support.'],
].map(([name, public_url, short_description], index) => ({
  id: `static-unit-${index}`,
  page_type: 'unit',
  name,
  slug: public_url.replace(/^\//, ''),
  parent_group: 'Administrative Units',
  short_description,
  public_url,
  hero_image: '/fpb.png',
  legacy: true,
}));

const STATIC_DIRECTORATES = [
  ['Directorate of Communication and Related Matters', '/directorate-of-communication-and-related-matters', 'Coordinates communication, information dissemination, public relations and related institutional matters.'],
  ['Directorate of Continuing Education (DCE)', '/directorate-of-continuing-education-dce-', 'Coordinates continuing education programmes, flexible learning pathways and lifelong learning services.'],
  ['Directorate of Gender Issues', '/directorate-of-gender-issues', 'Supports gender equity, inclusion, advocacy and campus sensitisation activities.'],
  ['Directorate of Information and Technological Services (ITS)', '/directorate-of-information-and-technological-services-its-', 'Coordinates ICT infrastructure, digital platforms, information systems and technology support.'],
  ['Directorate of Public Procurement', '/directorate-of-public-procurement', 'Supports procurement planning, compliance, due process and procurement documentation.'],
  ['Directorate of TETFund Intervention and Other Grants', '/directorate-of-tetfund-intervention-and-other-grants', 'Coordinates TETFund interventions, grant records, project tracking and related institutional development support.'],
  ['Directorate of Works and Services', '/directorate-of-works-and-services', 'Coordinates works, facilities, maintenance, infrastructure support and physical services.'],
  ['Directorate for Technical and Vocational Education Training', '/directorate-for-teaching-and-vocational-education-and-training', 'Supports technical, vocational, skills-based training and capacity development.'],
  ['Directorate of Physical Planning and Development (PPD)', '/directorate-of-physical-planning-and-development-ppd-', 'Coordinates physical planning, campus development and infrastructure planning support.'],
  ['Directorate of Academic Planning Unit', '/directorate-of-academic-planning-unit', 'Supports academic planning, programme development, accreditation and institutional academic data.'],
  ['Directorate of Student Industrial Placement Unit (IPU)', '/directorate-of-student-industrial-placement-unit-ipu-', 'Coordinates industrial placement, SIWES support, industry liaison and student workplace exposure.'],
].map(([name, public_url, short_description], index) => ({
  id: `static-directorate-${index}`,
  page_type: 'directorate',
  name,
  slug: public_url.replace(/^\//, ''),
  parent_group: 'Strategic Directorates',
  short_description,
  public_url,
  hero_image: '/fpb.png',
  legacy: true,
}));

const STATIC_BY_TYPE = {
  department: STATIC_DEPARTMENTS,
  center: STATIC_CENTERS,
  unit: STATIC_UNITS,
  directorate: STATIC_DIRECTORATES,
};

export default function InstitutionalDirectory({ pageType = 'department' }) {
  const config = CONFIG[pageType] || CONFIG.department;
  const Icon = config.icon;
  const reduceMotion = useReducedMotion();
  const [items, setItems] = useState([]);
  const [legacyItems, setLegacyItems] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        setLoading(true);
        setError('');
        const res = await fetch(`${API_BASE}/api/institutional-pages?type=${pageType}`);
        const data = res.ok ? await res.json() : [];
        if (alive) setItems(Array.isArray(data) ? data : []);

        if (pageType === 'department') {
          const legacyRes = await fetch(`${API_BASE}/api/departments`);
          const legacy = legacyRes.ok ? await legacyRes.json() : [];
          if (alive) setLegacyItems(Array.isArray(legacy) ? legacy : []);
        }
      } catch (err) {
        if (alive) setError('Unable to load pages at the moment.');
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => { alive = false; };
  }, [pageType]);

  const mergedItems = useMemo(() => {
    const mappedLegacy = legacyItems.map((item) => ({
      id: `legacy-${item.id}`,
      page_type: 'department',
      name: item.name,
      slug: item.slug,
      parent_group: item.school,
      short_description: item.description,
      hero_image: item.hero_image,
      head_name: item.hod_name,
      public_url: `/departments/${item.slug}`,
      legacy: true,
    }));
    const staticItems = STATIC_BY_TYPE[pageType] || [];
    const bySlug = new Map();
    [...staticItems, ...items, ...mappedLegacy].forEach((item) => {
      const key = item.slug || String(item.public_url || item.name || '').replace(/^\//, '');
      if (!key) return;
      if (!bySlug.has(key)) bySlug.set(key, item);
    });
    const all = [...bySlug.values()];
    const term = q.trim().toLowerCase();
    if (!term) return all;
    return all.filter((item) => [item.name, item.parent_group, item.short_description, item.head_name].join(' ').toLowerCase().includes(term));
  }, [items, legacyItems, q, pageType]);

  return (
    <main className="institutional-directory min-h-screen bg-slate-50 text-slate-950">
      <motion.section initial={reduceMotion ? false : { opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.45 }} className={`institutional-directory-hero relative overflow-hidden bg-gradient-to-r ${config.accent} px-4 py-20 text-white`}>
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, white 0, transparent 26%), radial-gradient(circle at 80% 10%, white 0, transparent 22%)' }} />
        <motion.div variants={directoryStagger} initial="hidden" animate="visible" className="relative mx-auto max-w-7xl">
          <motion.div variants={directoryFade} className="inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-sm font-black backdrop-blur">
            <Icon size={18} /> {config.eyebrow}
          </motion.div>
          <motion.h1 variants={directoryFade} className="mt-6 max-w-4xl text-4xl font-black tracking-tight md:text-6xl">{config.title}</motion.h1>
          <motion.p variants={directoryFade} className="mt-5 max-w-3xl text-lg leading-8 text-white/90">{config.subtitle}</motion.p>
        </motion.div>
      </motion.section>

      <section className="mx-auto max-w-7xl px-4 py-10">
        <motion.div variants={directoryFade} initial="hidden" whileInView="visible" viewport={{ once: true }} className="institutional-directory-filter mb-8 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm md:flex md:items-center md:justify-between md:gap-4">
          <div>
            <h2 className="text-xl font-black">Browse {config.title}</h2>
            <p className="mt-1 text-sm text-slate-600">{mergedItems.length} published page{mergedItems.length === 1 ? '' : 's'} available.</p>
          </div>
          <label className="mt-4 flex min-w-0 items-center gap-3 rounded-2xl border border-slate-200 px-4 py-3 md:mt-0 md:w-96">
            <Search size={18} className="text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${config.title.toLowerCase()}...`} className="min-w-0 flex-1 bg-transparent text-sm outline-none" />
          </label>
        </motion.div>

        {error && <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-bold text-red-700">{error}</div>}
        {loading ? (
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {[1,2,3,4,5,6].map((i) => <div key={i} className="h-72 animate-pulse rounded-3xl bg-white shadow-sm" />)}
          </div>
        ) : mergedItems.length ? (
          <motion.div variants={directoryStagger} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }} className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {mergedItems.map((item) => (
              <motion.div key={item.id} variants={directoryFade} whileHover={reduceMotion ? undefined : { y: -8 }} transition={{ type: "spring", stiffness: 135, damping: 18 }}>
              <Link to={item.public_url || `/${config.plural}/${item.slug}`} className="group overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 transition hover:-translate-y-1 hover:shadow-xl">
                <div className="h-48 overflow-hidden bg-slate-100">
                  <img src={publicImage(item.hero_image)} alt="" className="h-full w-full object-cover transition duration-500 group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <div className="mb-3 flex flex-wrap gap-2">
                    <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-black text-emerald-700">{item.page_type || pageType}</span>
                    {item.parent_group && <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600">{item.parent_group}</span>}
                  </div>
                  <h3 className="text-xl font-black leading-tight text-slate-950 group-hover:text-emerald-700">{item.name}</h3>
                  <p className="mt-3 line-clamp-3 text-sm leading-6 text-slate-600">{item.short_description || 'View overview, leadership, services, resources and contact information.'}</p>
                  {item.head_name && <p className="mt-4 text-xs font-black uppercase tracking-wider text-slate-500">Head: {item.head_name}</p>}
                </div>
              </Link>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <div className="rounded-3xl border border-slate-200 bg-white p-10 text-center shadow-sm">
            <h3 className="text-xl font-black">No published {config.title.toLowerCase()} yet.</h3>
            <p className="mt-2 text-slate-600">Admin can publish pages from the Institutional Pages CMS.</p>
          </div>
        )}
      </section>
    </main>
  );
}
